import { defineStore } from 'pinia';

// Per-tab, same as viewAs.js's VIEW_AS_SESSION_KEY — the audited impersonation
// session should survive a refresh alongside the view-as session it belongs to,
// not get re-minted on every page load.
const IMPERSONATION_SESSION_KEY = 'eaImpersonationSession';

function readImpersonationSession() {
  try {
    const raw = sessionStorage.getItem(IMPERSONATION_SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeImpersonationSession(session) {
  try {
    if (session) sessionStorage.setItem(IMPERSONATION_SESSION_KEY, JSON.stringify(session));
    else sessionStorage.removeItem(IMPERSONATION_SESSION_KEY);
  } catch {
    // Storage unavailable — the session lasts for this page load only.
  }
}

export const useUserStore = defineStore('user', {
  state: () => {
    const persistedImpersonation = readImpersonationSession() || {};

    return {
      welcomePageData: {},
      apis: {
        learnerData: {}
      },
      adminUserName: '',
      IDVerifyCapturedFaceImage: '',
      IDVerifyCapturedIDPhoto: '',
      IDVerifyHighResCapturedFaceImage: '',
      IDVerifyHighResCapturedIDPhoto: '',
      // Admin context when using "View As" feature
      adminContext: {
        asurite: null,
        role: null
      },
      // Hydrated at creation so a refresh resumes the same audited session
      // instead of minting a new one (getImpersonationToken self-expires it).
      impersonationSession: {
        token: persistedImpersonation.token || null,
        expiresAt: persistedImpersonation.expiresAt || null,
        sessionId: persistedImpersonation.sessionId || null,
        targetLearnerId: persistedImpersonation.targetLearnerId || null,
        targetAsurite: persistedImpersonation.targetAsurite || null
      }
    };
  },

  actions: {
    setPartnerData(payload) {
      this.welcomePageData = payload;
    },
    setLearnerData(payload) {
      this.apis.learnerData = payload;
    },
    setFaceImage(payload) {
      this.IDVerifyCapturedFaceImage = payload;
    },
    setIDImage(payload) {
      this.IDVerifyCapturedIDPhoto = payload;
    },
    setHighResFaceImage(payload) {
      this.IDVerifyHighResCapturedFaceImage = payload;
    },
    setHighResIDImage(payload) {
      this.IDVerifyHighResCapturedIDPhoto = payload;
    },
    setAdminUserName(name) {
      this.adminUserName = name;
    },
    setAdminContext(asurite, role = 'Admin') {
      this.adminContext.asurite = asurite;
      this.adminContext.role = role;
    },
    clearAdminContext() {
      this.adminContext.asurite = null;
      this.adminContext.role = null;
    },
    setImpersonationSession(session) {
      this.impersonationSession.token = session?.token || null;
      this.impersonationSession.expiresAt = session?.expiresAt || null;
      this.impersonationSession.sessionId = session?.sessionId || null;
      this.impersonationSession.targetLearnerId = session?.targetLearnerId || null;
      this.impersonationSession.targetAsurite = session?.targetAsurite || null;
      writeImpersonationSession(session ? { ...this.impersonationSession } : null);
    },
    clearImpersonationSession() {
      this.impersonationSession.token = null;
      this.impersonationSession.expiresAt = null;
      this.impersonationSession.sessionId = null;
      this.impersonationSession.targetLearnerId = null;
      this.impersonationSession.targetAsurite = null;
      writeImpersonationSession(null);
    }
  },

  getters: {
    getWelcomeTitle: (state) => state.welcomePageData.studentPortalTitle || '',
    getPartnerLogo: (state) => state.welcomePageData.partnerLogo || '',
    getLearnerData: (state) => state.apis.learnerData,
    getImpersonationToken: (state) => {
      if (!state.impersonationSession.token || !state.impersonationSession.expiresAt) {
        return null;
      }

      const expiresAt = new Date(state.impersonationSession.expiresAt);
      if (Number.isNaN(expiresAt.getTime()) || expiresAt <= new Date()) {
        return null;
      }

      return state.impersonationSession.token;
    }
  }
});
